import { URIS, Kind } from 'fp-ts/lib/HKT'
import { record, array, option, apply, task } from 'fp-ts'
import { SorterResult, SortOrder } from 'antd/lib/table/interface'
import { pipe } from 'fp-ts/lib/function'

import * as React from 'react'
export * from './Codec'
export * from './form'
export * from './xlsx'
export * from './PBL'

export type ValueOf<T> = T[keyof T]

export type Unpacked<T> = U<U<U<U<U<U<T>>>>>>

export type U<T> = T extends (...args: any[]) => infer U
  ? U
  : T extends Promise<infer U>
  ? U
  : T

export type HKTStruct<URI extends URIS, O extends object> = {
  [K in keyof O]: Kind<URI, O[K]>
}

export const mapStruct =
  <A, B>(fab: (_: A) => B) =>
  <O extends { [_: string]: A }>(o: O): { [k in keyof O]: B } =>
    // @ts-ignore
    record.map(fab)(o)

export const sortFromSorterResult = <
  K extends string,
  T extends Record<K, any>
>(
  sorter: SorterResult<T> | SorterResult<T>[]
) =>
  pipe(
    sorter instanceof Array ? array.head(sorter) : option.some(sorter),
    option.chain(s =>
      apply.sequenceT(option.Monad)(
        pipe(
          option.fromNullable(s.field),
          option.chain(option.fromPredicate((f): f is K => f !== 'key'))
        ),
        pipe(
          option.fromNullable(s.order),
          option.map(o => (o === 'ascend' ? 'asc' : 'desc'))
        )
      )
    ),
    option.getOrElse(() => undefined as undefined | [K, 'asc' | 'desc'])
  )

export const sortOrder = <K extends string>(
  k: K,
  sort?: [K, 'asc' | 'desc']
): SortOrder =>
  !sort || sort[0] !== k ? null : sort[1] === 'asc' ? 'ascend' : 'descend'

export const knownCurrency = (currency: string) => {
  try {
    new Intl.NumberFormat('en', { currency, style: 'currency' })
    return true
  } catch (e) {
    return false
  }
}

export const useIsKnownCurrency = (currency: string) => {
  const [isKnownCurrency, setIsKnownCurrency] = React.useState(false)

  React.useEffect(() => {
    setIsKnownCurrency(knownCurrency(currency))
  }, [currency])

  return isKnownCurrency
}

export type TaskInnerType<T> = T extends task.Task<infer R> ? R : never
